import React, { useState, useEffect } from 'react';
import './index.scss';
import { TimesIcon } from '@/icons';
import { useExtraTool } from '@/contexts/ExtraToolContext';

interface MaskingPageProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const MaskingPage: React.FC<MaskingPageProps> = ({ containerRef }) => {
  const [rect, setRect] = useState<Rect>({
    top: 20,
    left: 20,
    width: 60,
    height: 40,
  });
  const {closeMaskingPage } = useExtraTool();
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [activeHandle, setActiveHandle] = useState<string | null>(null);

  // Sürükleme başlangıcı
  const handleMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    direction: string,
  ) => {
    setIsResizing(true);
    setActiveHandle(direction);
    e.preventDefault(); // Tarayıcı varsayılanını engelle
  };

  // Sürükleme sırasında boyutu günceller
  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing || !containerRef?.current) return;

    const newRect = { ...rect };

    // Parent boyutlarını al
    const parentRect = containerRef.current.getBoundingClientRect();
    const parentWidth = parentRect.width;
    const parentHeight = parentRect.height;

    if (activeHandle === 'bottom') {
      newRect.height = Math.min(
        Math.max(
          ((e.clientY - parentRect.top) / parentHeight) * 100 - rect.top,
          10,
        ),
        100 - rect.top,
      );
    } else if (activeHandle === 'top') {
      const newTop = Math.min(
        Math.max(((e.clientY - parentRect.top) / parentHeight) * 100, 0),
        rect.top + rect.height - 10,
      );
      newRect.height += rect.top - newTop;
      newRect.top = newTop;
    } else if (activeHandle === 'right') {
      newRect.width = Math.min(
        Math.max(
          ((e.clientX - parentRect.left) / parentWidth) * 100 - rect.left,
          10,
        ),
        100 - rect.left,
      );
    } else if (activeHandle === 'left') {
      const newLeft = Math.min(
        Math.max(((e.clientX - parentRect.left) / parentWidth) * 100, 0),
        rect.left + rect.width - 10,
      );
      newRect.width += rect.left - newLeft;
      newRect.left = newLeft;
    }

    setRect(newRect);
  };

  // Sürükleme bittiğinde işlemi sonlandırır
  const handleMouseUp = () => {
    setIsResizing(false);
    setActiveHandle(null);
  };

  // Global mouse event'lerini yönetmek için useEffect kullanıyoruz
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  return (
    <div
      className="overlay-mask"
      style={{
        clipPath: `polygon(
            0 0,
            100% 0,
            100% 100%,
            0 100%,
            0 0,
            ${rect.left}% ${rect.top}%,
            ${rect.left}% ${rect.top + rect.height}%,
            ${rect.left + rect.width}% ${rect.top + rect.height}%,
            ${rect.left + rect.width}% ${rect.top}%,
            ${rect.left}% ${rect.top}%
          )`,
      }}
    >
      <button
        className="close-button"
        onClick={() => {
          closeMaskingPage();
        }}
      >
        <TimesIcon className={undefined} />
      </button>
      <div
        className="resize-handle top"
        style={{
          top: `${rect.top}%`,
          left: `${rect.left}%`,
          width: `${rect.width}%`,
        }}
        onMouseDown={(e) => handleMouseDown(e, 'top')}
      ></div>
      <div
        className="resize-handle bottom"
        style={{
          top: `${rect.top + rect.height}%`,
          left: `${rect.left}%`,
          width: `${rect.width}%`,
        }}
        onMouseDown={(e) => handleMouseDown(e, 'bottom')}
      ></div>
      <div
        className="resize-handle left"
        style={{
          top: `${rect.top}%`,
          left: `${rect.left}%`,
          height: `${rect.height}%`,
        }}
        onMouseDown={(e) => handleMouseDown(e, 'left')}
      ></div>
      <div
        className="resize-handle right"
        style={{
          top: `${rect.top}%`,
          left: `${rect.left + rect.width}%`,
          height: `${rect.height}%`,
        }}
        onMouseDown={(e) => handleMouseDown(e, 'right')}
      ></div>
    </div>
  );
};

export default MaskingPage;
